import "firebase/database";
import firebase from "firebase/app";
import { Base64 } from "js-base64";
import { debounce, throttle, getFileBuffer, getIOSDetails, rightTrim } from "./util";
import { Device, Call } from '@twilio/voice-sdk';

import axios, { AxiosInstance } from "axios";
import * as env from '@/common/environment';
import * as PleasedApi from '@/common/api/api.pleased'

import eventSourcePoly from "event-source-polyfill";
import { FirebaseChatMessage } from "./types";
import { ReportedEvent, reportingService } from "@/common/reporting";
import { Chat, ActiveOpenChat, SystemMessageConfig, VerifyChatOpenDto, MessageAttachment } from '@/common/api/models.pleased';
import { event } from "vue-gtag";
import { GA_EVENT_NAME_ENUM } from "@/common/gaHelper";
import { ErrorCode, parseErrorMessage, ThresholdLimitError } from "@/common/error";
import { analytics, CHAT_EVENTS, CMP_CONTEXTS, COMPONENT_EVENTS, CRITICAL_EVENTS, EVENT_TYPES, INPUT_EVENTS, VOICE_EVENTS } from "@/common/logs";
import { SystemTriggerMessageBroker } from "@/common/systemMessages";
import { randomUUID } from "@/common/bot/lib";
import { removeLimitedData } from "@/common/utils";

// end user status polling
const USER_STATUS_UPDATE_TIME = 15 * 1000; // 15 seconds

const RANDOM_AGENT_ID = Math.floor(Math.random() * 100000 + 1);
const AGENT_NICK_DEFAULT = `agent:${RANDOM_AGENT_ID}`;
const SYSTEM_TRIGGER_ADMIN = 1;

const VOICE_SAMPLE_TRACK_INTERVAL = 10_000; // keep existing 10s interval
const VOICE_SAMPLE_MAX_SAMPLES = 10; // max number of sample events to track per call
const SILENT_CALL_THRESHOLD = 10; // number of consecutive samples with no packetsSent/bytesSent to trigger SILENT_CALL event

const Tags = {
  /**
   * Set true if we want to remove unsupported system tags.
   */
  REMOVE_UNSUPPORTED_SYSTEM_TAGS: false,
  /**
   * Set true if we don't want to forward normal tags.
   */
  REMOVE_RAW_TAGS: false,

  SUPPORTED_SYSTEM_TAGS: ['language', 'country'],
  SYSTEM_TAG_REGEX: /(^[ A-Za-z0-9_@#&+-]+)=([ A-Za-z0-9_@./#&+-]*$)/i,

  isSystemTag(tag: string) {
    return Tags.SYSTEM_TAG_REGEX.test(tag);
  },
  isSupported(key: string) {
    return Tags.SUPPORTED_SYSTEM_TAGS.includes(key)
  },
  parse(tag: string) {
    const [, key = '', value = ''] = tag.match(Tags.SYSTEM_TAG_REGEX) || [];
    if (!key) {
      console.error('Invalid system tag', tag);
    }
    return [key, value];
  },
  clean(tags: string[]) {
    if (!tags?.length) return [];
    return tags.map(s => (s || '')?.trim()).filter(Boolean).filter(tag => {
      if (!Tags.isSystemTag(tag)) return Tags.REMOVE_RAW_TAGS;
      if (!Tags.REMOVE_UNSUPPORTED_SYSTEM_TAGS) return true;
      const [key] = Tags.parse(tag);
      return Tags.isSupported(key);
    }).map(s => s.toLowerCase())
  }
}

const warnNonNull = (value: null | any, varName: string) => (value === null || value === undefined) && console.warn(`Expected ${varName} to be non null.`); // TODO: RNL

const transformFile = (data: {
  from: string;
  attachmentName: string;
  attachmentUrl: string;
  contentType: string;
  attachmentFlag: boolean;
  createdAt?: Date | number | string;
  to: string;
  message: string;
  userId: string;
}) => ({
  type: "chat.file",
  display_name:
    data.from === null ? AGENT_NICK_DEFAULT : `${data.from}:${RANDOM_AGENT_ID}`,
  nick:
    data.from === null ? AGENT_NICK_DEFAULT : `${data.from}:${RANDOM_AGENT_ID}`,
  timestamp: new Date(),
  attachment: {
    name: data.attachmentName,
    url: data.attachmentUrl,
    mime_type: data.contentType,
    attachmentFlag: data.attachmentFlag,
    createdAt: data.createdAt,
    from: data.from,
    to: data.to,
    message: data.message,
    userId: data.userId
  },
  source: "local"
});

interface CustomTransactionMessage {
  messageKind: string;
  transactionKind: string;
  transactionFilter: string;
  options: unknown[]
}


const transformMessage = (data: { from: string, message: string } & Partial<CustomTransactionMessage>) => ({
  type: "chat.msg",
  display_name:
    data.from === null ? AGENT_NICK_DEFAULT : `${data.from}:${RANDOM_AGENT_ID}`,
  nick:
    data.from === null ? AGENT_NICK_DEFAULT : `${data.from}:${RANDOM_AGENT_ID}`,
  timestamp: new Date(),
  msg: data.message,
  source: "local",
  ... data.messageKind ? ({
    meta: {
      messageKind: data.messageKind,
      transactionKind: data.transactionKind,
      options: data.options
    }
  }): {}
});


type UserTypingList = Record<
  string | number,
  {
    displayName: "Customer";
    isAgent: boolean;
    isTyping: boolean;
  }
>;

type SelectedTransaction = { type: string, status: string, txId: string };


class PleasedChatClient {
  pleased_url: string;
  brand: any;
  pleasedAccessToken: string;
  name: string;
  email: string;
  phone: string;
  foreignId: string;
  firebaseApp: firebase.app.App | null;
  firebaseDatabaseRef: firebase.database.Reference | null;
  firebaseEndedChatsDatabaseRef: firebase.database.Reference | null;
  firebaseUserTypingRef: firebase.database.Reference | null;
  firebaseEndUserStatusRef: firebase.database.Reference | null;
  firebaseAiChatEndUserStatusRef: firebase.database.Reference | null;
  firebaseAiChatInterveneRef: firebase.database.Reference | null;
  firebaseConnectionRef: firebase.database.Reference | null;
  usersTypingList: UserTypingList;
  unsentMessages: FirebaseChatMessage[];
  endChatTicketSubject: string;
  endChatTicketSubjectSet: boolean;
  eventCallbacks: Record<string, Function[]>;
  autoReplyEnabled: boolean;
  autoReplyMessage: string;
  agentDisplayName: string;
  chatAgentsList: string[];
  queuePositionSource: EventSource | null;
  userStatusPollingId: number | null;
  twilioDevice: Device | null;
  axios!: AxiosInstance;
  chatId?: number;
  aiSessionId?: string;
  customerId!: number;
  firebaseToken?: string;
  firebaseChatMetaData: any;
  display_name: any;
  selectedTransactions: SelectedTransaction[];
  systemMessageBroker: SystemTriggerMessageBroker | null = null;
  resumableChatSessionId: string = '';
  call: Call | null = null;
  silentCallCount: number = 0;
  silentCallEventSent: boolean = false;
  hasNonEmptySampleBeforeThreshold: boolean = false;

  constructor(options?: { pleased_url: string; brand: any }) {
    let pleased_url;
    let brand;

    if (typeof options !== "undefined") {
      if (options && options.pleased_url) pleased_url = options.pleased_url;
      if (options && options.brand) brand = options.brand;
    }

    this.pleased_url = pleased_url || env.get('url'); //  allEnvs[CURRENT_ENV].PLEASED_URL;
    this.brand = brand || env.get('brandCode'); // allEnvs[CURRENT_ENV].BRAND;

    this.pleasedAccessToken = "";
    this.name = "";
    this.email = "";
    this.phone = "";
    this.foreignId = "";

    this.firebaseApp = null;
    this.firebaseDatabaseRef = null;
    this.firebaseEndedChatsDatabaseRef = null;
    this.firebaseUserTypingRef = null;
    this.usersTypingList = {};
    this.unsentMessages = [];
    this.endChatTicketSubject = "";
    this.endChatTicketSubjectSet = false;
    this.firebaseEndUserStatusRef = null;
    this.firebaseAiChatEndUserStatusRef = null;
    this.firebaseAiChatInterveneRef = null;
    this.firebaseConnectionRef = null;

    this.eventCallbacks = {};
    this.autoReplyEnabled = false;
    this.autoReplyMessage = "";
    this.agentDisplayName = "";
    this.chatAgentsList = [];
    this.queuePositionSource = null;
    this.userStatusPollingId = null;
    this.twilioDevice = null;
    this.selectedTransactions = [];
  }

  // [Chat, Voice] init the required params
  init({
    access_token,
    pleased_url,
    brand_subdomain,
  }: {
    access_token: string;
    pleased_url?: string;
    brand_subdomain?: string;
  }) {
    // Not used any longer.
    this.pleasedAccessToken = access_token;
    this.brand =
      typeof brand_subdomain === "undefined" ? this.brand : brand_subdomain;
    this.pleased_url = rightTrim(typeof pleased_url === "undefined" ? this.pleased_url : pleased_url, '/');
    console.log(this.pleased_url)

    this.axios = axios.create({
      baseURL: this.pleased_url,
      headers: {
        "pl-access-token": this.pleasedAccessToken,
        "brand-subdomain": this.brand
      },
      timeout: 10000
    });

    try {
      this.queuePositionSource?.close();
    } catch (error) {
      // pass through
    }

    return this;
  }

  // [Chat, Voice] set visitor information which will be used in lots of APIs and Firebaase
  async setVisitorInfo(
    {
      display_name,
      email,
      phone,
      foreign_id,
      customerAppVersion = "",
      ticketId,
      chatTags,
      autoAddTags = [],
      subject = '',
      selectedTransactions = [],
      aiSessionId,
      isGuest
    }: {
      display_name: string;
      email: string;
      phone: string;
      foreign_id: string;
      customerAppVersion: string;
      ticketId?: string | number;
      chatTags?: string[];
      autoAddTags?: string[];
      subject?: string;
      selectedTransactions?: SelectedTransaction[];
      aiSessionId?: string;
      isGuest?: boolean;
    },
    isVoice = false // TODO: if is true, it will create chat request, could separate setVisitorInfo to 1. setVisitorInfo and 2. requestChat
  ) {
    this.setVisitorDetails({ display_name, phone, email, foreign_id });

    this.chatId = 0;
    this.customerId = 0;
    this.firebaseToken = "";

    if (isVoice) return Promise.resolve();

    const body: {
      email: string;
      name: string;
      phone: string;
      categoryId: number;
      foreignId: string;
      customerAppVersion: string;
      ticketId?: string | number;
      systemTags?: string[];
      userLogs?: ReportedEvent[];
      subject?: string;
      transactions: SelectedTransaction[];
      chatbotSessionId?: string;
      aiSessionId?: string;
      isGuest?: boolean;
    } = {
      email: this.email,
      name: this.name,
      phone: this.phone,
      categoryId: 0,
      foreignId: this.foreignId,
      customerAppVersion,
      transactions: selectedTransactions,
      chatbotSessionId: analytics.sessionId,
      aiSessionId,
      isGuest
    };

    if (ticketId) body.ticketId = ticketId
    if (subject) body.subject = subject
    if (customerAppVersion) {
      body.customerAppVersion = customerAppVersion;
    }
    if (chatTags?.length) body.systemTags = [...autoAddTags, ...Tags.clean(chatTags)];

    reportingService.chatInitiated();
    event(GA_EVENT_NAME_ENUM.START_LIVE_CHAT_CONNECTION);

    const userLogs = reportingService.getLogs();
    if (userLogs.length) body.userLogs = userLogs;

    analytics.track({
      name: `${CMP_CONTEXTS.CONNECT_REQUEST}${COMPONENT_EVENTS.CONNECT_OPTIONS_CHAT_INITIATED}`,
      selection: ticketId || null,
      metadata: { body }
    });

    try {
      const response = await this.axios.post("/api/v1/chat/customer-request", body);
      const systemTriggers = response?.data?.data?.timeSinceTriggers;
      this.brokerCreation(systemTriggers);
      if (response && response.data && response.data.code !== 10000) {
        if (response.data.code === ErrorCode.THRESHOLD_LIMIT) {
          event(GA_EVENT_NAME_ENUM.LIVE_CHAT_CONNECTION_FAILED);
          throw new ThresholdLimitError();
        }

        event(GA_EVENT_NAME_ENUM.LIVE_CHAT_CONNECTION_FAILED);

        throw new Error(
          `Bad Response from Server; Code = ${response.data.code}`
        );
      }

      if (response && response.data && response.data.data) {
        const context = ticketId? CMP_CONTEXTS.ACTIVE_CONVERSATION_TICKET : CMP_CONTEXTS.CONNECT_REQUEST;

        analytics.track({
          name: `${context}${COMPONENT_EVENTS.CONNECT_OPTIONS_CHAT_CREATED}`,
          // TODO: check if has response.data.data.chatId
          ...(ticketId ? { selection: ticketId } : { selection: response.data.data.chatId }),
        })
        event(GA_EVENT_NAME_ENUM.LIVE_CHAT_CONNECTED);
        this.setupFirebaseChat(response.data)
        return;
      }

      throw new Error("No Response from Server");
    } catch (err) {
      console.error("Error in setting visitor info", err);
      event(GA_EVENT_NAME_ENUM.LIVE_CHAT_CONNECTION_FAILED);
      throw err;
    }
  }

  resumeChat(chat: ActiveOpenChat, visitor: { display_name: string, phone: string, email: string, foreign_id: string }, message: string, canBeResumed?: VerifyChatOpenDto|null) {
    this.resumableChatSessionId = randomUUID();
    this.brokerCreation(canBeResumed?.timeSinceTriggers || [], this.resumableChatSessionId);
    this.setVisitorDetails(visitor);
    this.setupFirebaseChat({
      data: {
        autoReplyMessage: '',
        isAutoReplyEnabled: false,
        chatId: chat.id,
        customerId: chat.customerId,
        firebaseToken: chat.firebaseToken
      },
      resume: true,
      resumeMessage: message,
    })

  }

  protected brokerCreation(systemTriggers: SystemMessageConfig[], resumableChatSessionId?: string) {
    const broker = new SystemTriggerMessageBroker(systemTriggers, resumableChatSessionId);
    this.systemMessageBroker = broker;
    broker.onMatch(match => {
      this.sendSystemMessage(match.message || '');
    });
  }

  protected sendSystemMessage(message: string) {
    const chatMessage: FirebaseChatMessage = {
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      from: "agent",
      attachmentFlag: false,
      attachmentUrl: "",
      userId: SYSTEM_TRIGGER_ADMIN,
      message,
      to: "customer",
    }
    warnNonNull(this.firebaseDatabaseRef, 'this.firebaseDatabaseRef'); // TODO: RNL
    const newRef = this.firebaseDatabaseRef?.push();
    if (newRef) {
      newRef.set(chatMessage);
    } else {
      throw new Error('Pleased Client is not ready.')
    }
  }

  protected setVisitorDetails({ display_name, phone, email, foreign_id }: { display_name: string, phone: string, email: string, foreign_id: string }) {
    this.name = display_name || phone || email;
    this.email = email;
    this.phone = phone;
    this.foreignId = foreign_id;
  }

  protected setUserPollingInterval(ref: firebase.database.Reference | null) {
    // register on disconnect event
    ref
      ?.onDisconnect()
      .update({ status: 'offline', lastStatusUpdateTime: firebase.database.ServerValue.TIMESTAMP }, err => {
        if (!err) {
          // no op
        } else {
          console.error('disconnect event registration failed!');
        }
      });

    const pollingId = (setInterval(() => {
      try {
        ref?.child('lastStatusUpdateTime').set(firebase.database.ServerValue.TIMESTAMP);
      } catch (err) {
        clearInterval(pollingId);
        console.error('chat polling stopped!', err);
      }
    }, USER_STATUS_UPDATE_TIME) as unknown) as number; // TODO: Fix the types in tsconfig

    return pollingId;
  }


  protected setupFirebaseUserStatus(options: {
    ref: firebase.database.Reference | null;
    chatId?: number | string;
    customerId: number;
    onMemberLeave?: () => void;
  }) {
    const { ref, chatId, customerId, onMemberLeave } = options;

    try {
      // set initial end user status
      ref?.set({
        status: 'online',
        chatId: chatId,
        customerId: customerId,
        lastStatusUpdateTime: firebase.database.ServerValue.TIMESTAMP
      });
    } catch (err) {
      console.error('unable to create end user status', err);
    }

    // useful when agent ends the chat
    ref?.on('value', snap => {
      const endStatusValue = snap.exists();
      if (!endStatusValue) {
        // if agent ends the chat, chat value will be null

        try {
          onMemberLeave?.();
          ref?.onDisconnect().cancel();
          this.userStatusPollingId && clearInterval(this.userStatusPollingId);
        } catch (err) {
          console.error('chat cleanup failed!', err);
        }
      } else if (!snap.val().chatId) {
        // chat do not exists, chat data found due to firebase sync

        try {
          ref?.onDisconnect().cancel();
          // remove that data because no longer needed
          ref?.remove();
          onMemberLeave?.();
          this.userStatusPollingId && clearInterval(this.userStatusPollingId);
        } catch (err) {
          console.error('chat cleanup failed!', err);
        }
      }
    });

    // start initial polling
    this.userStatusPollingId = this.setUserPollingInterval(ref);
  }

  setupFirebaseChat(response: { data: Chat, resume?: boolean, resumeMessage?: string }) {

    const { data, resume: isResumableChat, resumeMessage } = response;

    this.chatId = data.chatId;
    this.customerId = data.customerId;
    this.firebaseToken = data.firebaseToken;
    this.autoReplyEnabled = data.isAutoReplyEnabled;
    this.autoReplyMessage = data.autoReplyMessage;

    this.firebaseApp = firebase.initializeApp(
      {
        apiKey: this.firebaseToken,
        authDomain: env.get('firebase').auth, // allEnvs[CURRENT_ENV].FIREBASE_AUTH_DOMAIN,
        databaseURL: env.get('firebase').database,  //allEnvs[CURRENT_ENV].FIREBASE_DATABASE_URL,
        projectId: env.get('firebase').projectId // allEnvs[CURRENT_ENV].FIREBASE_PROJECT_ID
      },
      `chat-${this.chatId}`
    );

    this.firebaseDatabaseRef = this.firebaseApp
      .database()
      .ref(`chat/${this.chatId}`);

    this.firebaseEndedChatsDatabaseRef = this.firebaseApp
      .database()
      .ref(`endedChats/${this.chatId}`);

    this.firebaseChatMetaData = this.firebaseApp
      .database()
      .ref(`chatMetaData/${this.chatId}`);

    this.firebaseChatMetaData.set({
      customerName: this.name,
      customerId: this.customerId,
      customerPhone: this.phone,
      agentMessaged: false,
      typingMessage: {},
      assignedAgent: {},
      invitedAgents: []
    });

    this.firebaseEndUserStatusRef = this.firebaseApp
      .database()
      .ref(`endUserStatus/${this.chatId}`);

    // Setup Firebase user status with chat-specific callbacks
    this.setupFirebaseUserStatus({
      ref: this.firebaseEndUserStatusRef,
      chatId: this.chatId,
      customerId: this.customerId,
      onMemberLeave: () => {
        this.eventCallbacks.chat.forEach(chatCallback => {
          debounce(
            chatCallback({
              type: "chat.memberleave",
              nick: AGENT_NICK_DEFAULT,
              display_name: this.agentDisplayName,
              timestamp: new Date()
            })
          );
        });

        analytics.track({
          type: EVENT_TYPES.CHAT,
          name: CHAT_EVENTS.CHAT_ROOM_CHAT_MEMBER_LEFT,
        });
      },
    });

    // firebase ref to check if current user connected to firebase
    this.firebaseConnectionRef = this.firebaseApp
      .database()
      .ref(".info/connected");

    // listen for connection events
    this.firebaseConnectionRef.on("value", snap => {
      const isFBConnected = snap.val();

      if (isFBConnected) {
        // firebase connection active

        // clear local storage to avoid any dummy data getting conflict with firebase
        removeLimitedData()

        warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
        // get firebase data from http: fail safe
        const url = this.firebaseEndUserStatusRef?.toJSON();
        const AxiosInstance = this.axios.get(`${url}.json`);
        setTimeout(() => {
          AxiosInstance.then(response => {
            // check end user data
            if (response.data) {
              // if data exists check if chat id exists as well: fail safe
              // stop polling
              this.userStatusPollingId &&
                clearInterval(this.userStatusPollingId);
              if (response.data.chatId) {
                // chat still exists: not removed by cron
                try {
                  warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
                  // fail safe to overwrite disconnect handler, because user joined again
                  this.firebaseEndUserStatusRef
                    ?.update({ 
                        status: 'online',         
                        chatId: this.chatId,
                        customerId: this.customerId, 
                        lastStatusUpdateTime: firebase.database.ServerValue.TIMESTAMP 
                    })

                  // start polling again: end user status
                  this.userStatusPollingId = this.setUserPollingInterval(this.firebaseEndUserStatusRef);
                } catch (err) {
                  console.error("polling after reconnection failed!", err);
                }
              } else {
                // chat do not exists, chat data found due to firebase sync

                try {
                  warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
                  // remove that data because no longer needed
                  this.firebaseEndUserStatusRef?.remove();

                  this.firebaseEndUserStatusRef?.onDisconnect().cancel();
                } catch (err) {
                  console.error(
                    "chat clean up after reconnection failed!",
                    err
                  );
                }
              }
            } else {
              // chat data not found yet
              try {
                warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
                // cancel disconnect event because chat already ended
                this.firebaseEndUserStatusRef?.onDisconnect().cancel();

                // stop polling: end user status
                this.userStatusPollingId &&
                  clearInterval(this.userStatusPollingId);
              } catch (err) {
                console.error(
                  "end user status data not found, chat clean up after reconnection failed!",
                  err
                );
              }
            }
          }).catch(err => {
            console.error("fetch end chat status failed!", err);
          });
        }, 2000);
      } else {
        // firebase connection not active, no need to polling

        // stop polling: end user status
        this.userStatusPollingId && clearInterval(this.userStatusPollingId);
      }
    });

    this.firebaseUserTypingRef = this.firebaseApp
      .database()
      .ref(`chatMetaData/${this.chatId}/typingMessage`);

    const self = this;


    this.firebaseUserTypingRef.on('value', snap => {
      if (!snap.val()) return;
      this.usersTypingList = snap.val();
      this.notifyUserTyping();
    })

    this.firebaseUserTypingRef.child(`${this.customerId}`).onDisconnect().update({ isTyping: false })


    if (!isResumableChat) {
      this.queuePositionSource = new eventSourcePoly.EventSourcePolyfill(
        `${self.pleased_url}/api/v1/chat/queue-position?chatId=${self.chatId}`,
        {
          headers: {
            "pl-access-token": this.pleasedAccessToken,
            "brand-subdomain": this.brand
          }
        }
      );

      this.queuePositionSource.addEventListener(
        "chat-queue-position-event",
        (event: any) => {
          let positionData = JSON.parse(event.data).data.position;
          const chatCallbacks = self.eventCallbacks.chat || [];

          chatCallbacks.forEach(chatCallback => {
            chatCallback({
              type: "chat.queue_position",
              nick: "system.queue",
              queue_position: positionData,
              timestamp: new Date()
            });
          });
        }
      );
    }

    this.eventCallbacks.chat.forEach(chatCallback => {
      chatCallback({
        type: "chat.memberjoin",
        nick: this.name,
        display_name: this.name,
        timestamp: new Date()
      });
    });

    this.eventCallbacks.visitor_update?.forEach(visitorUpdateCallback => {
      visitorUpdateCallback({
        display_name: this.display_name,
        phone: this.phone,
        email: this.email,
        timestamp: new Date()
      });
    });

    let agentJoined = false;
    let agentUpdated = false;
    let autoReplyUpdated = false;

    if (
      agentJoined === false &&
      autoReplyUpdated === false &&
      this.autoReplyEnabled &&
      this.autoReplyMessage !== ""
    ) {
      this.eventCallbacks.chat.forEach(chatCallback => {
        chatCallback({
          type: "chat.msg",
          display_name: `${AGENT_NICK_DEFAULT}system_generated`,
          nick: `${AGENT_NICK_DEFAULT}system_generated`,
          timestamp: new Date(Date.now() + 1000),
          msg: this.autoReplyMessage,
          source: "local",
          chatId: this.chatId
        });
        autoReplyUpdated = true;
      });
    }

    this.firebaseDatabaseRef.once("value", snapshot => {
      if (this.unsentMessages.length) {
        let unsentMessage = this.unsentMessages.shift();
        while (unsentMessage) {
          warnNonNull(this.firebaseDatabaseRef, 'this.firebaseDatabaseRef'); // TODO: RNL
          const newLoopRef = this.firebaseDatabaseRef?.push();
          newLoopRef?.set({ ...unsentMessage, userId: this.customerId } as FirebaseChatMessage);
          unsentMessage = this.unsentMessages.shift();
        }
      }
      if (isResumableChat && resumeMessage) {
        // We can send a message
        const message: FirebaseChatMessage = {
          createdAt: firebase.database.ServerValue.TIMESTAMP,
          from: "customer",
          attachmentFlag: false,
          attachmentUrl: "",
          userId: this.customerId,
          message: resumeMessage,
          to: "agent",
          userSelection: false,
          kind: 'user-resumed-chat',
          resumableChatSessionId: this.resumableChatSessionId
        }

        this.firebaseDatabaseRef?.push(message)
      }
      this.isReady();
    });

    this.firebaseDatabaseRef.on("child_added", data => {
      if (!this.eventCallbacks.chat) return;

      this.systemMessageBroker?.notifyNewMessage(data.val());
      this.eventCallbacks.chat.forEach(chatCallback => {
        const packet = data.val();
        if (!packet || packet.userSelection) return;
        if (!packet.attachmentFlag && packet.message) {
          const toSend = transformMessage(packet);
          if (packet.from === "agent") {
            // check if the display name from packet is different than the already existing name then update the display name and set agentJoined as false so that the member join event is triggered
            const currentDisplayName =
              packet.agentDisplayName !== null &&
                packet.agentDisplayName !== ""
                ? packet.agentDisplayName
                : "Agent";
            if (
              currentDisplayName !== this.agentDisplayName &&
              !this.chatAgentsList.includes(currentDisplayName)
            ) {
              this.chatAgentsList.push(currentDisplayName);
              this.agentDisplayName = currentDisplayName;
              agentJoined = false;
            }
            if (agentJoined === false && packet.userId !== SYSTEM_TRIGGER_ADMIN) {
              // add 1s delay for the first agent message after joining
              toSend.timestamp = new Date(Date.now() + 1000);
              chatCallback({
                type: "chat.queue_position",
                nick: "system.queue",
                queue_position: 0,
                timestamp: new Date()
              });
              try {
                this.queuePositionSource?.close();
              } catch (err) {
                // pass through
              }
              chatCallback({
                type: "chat.memberjoin",
                nick: AGENT_NICK_DEFAULT,
                display_name: this.agentDisplayName,
                timestamp: new Date()
              });
              if (this.eventCallbacks.agent_update) {
                this.eventCallbacks.agent_update.forEach(
                  agentUpdateCallBack => {
                    if (agentUpdated === false) {
                      agentUpdateCallBack({
                        nick: AGENT_NICK_DEFAULT,
                        display_name: this.agentDisplayName,
                        timestamp: new Date()
                      });
                    }
                    agentUpdated = true;
                  }
                );
              }
              agentJoined = true;

              analytics.track({
                type: EVENT_TYPES.CHAT,
                name: CHAT_EVENTS.CHAT_ROOM_CHAT_MEMBER_JOINED,
              });
            }
            if (packet.chatAssigned && !agentJoined) {
              chatCallback({
                type: "chat.queue_position",
                nick: "system.queue",
                queue_position: 0,
                timestamp: new Date()
              });

              try {
                this.queuePositionSource?.close();
              } catch (err) {
                // pass through
              }
            }
            chatCallback({...toSend, chatId: this.chatId, resumableChatSessionId: this.resumableChatSessionId, packetResumableChatSessionId: packet.resumableChatSessionId});
          } else {
            chatCallback({
              ...transformMessage(packet),
              type: 'chat.msg',
              display_name: this.display_name,
              nick: 'visitor:',
              timestamp: packet.timestamp,
              msg: toSend.msg,
              source: 'local',
              chatId: this.chatId,
              resumableChatSessionId: this.resumableChatSessionId,
              packetResumableChatSessionId: packet.resumableChatSessionId
            })

            if (!packet.userSelection && packet.kind !== 'user-resumed-chat') {
              analytics.track({
                type: EVENT_TYPES.INPUT,
                name: INPUT_EVENTS.CHAT_ROOM_END_USER_SENT_MESSAGE,
              });
            }
          }
        } else if (packet.attachmentFlag && packet.attachmentUrl) {
          const toSend = transformFile(packet);
          if (packet.from === "agent") {
            // check if the display name from packet is different than the already existing name then update the display name and set agentJoined as false so that the member join event is triggered
            const currentDisplayName =
              packet.agentDisplayName !== null &&
                packet.agentDisplayName !== ""
                ? packet.agentDisplayName
                : "Agent";
            if (
              currentDisplayName !== this.agentDisplayName &&
              !this.chatAgentsList.includes(currentDisplayName)
            ) {
              this.chatAgentsList.push(currentDisplayName);
              this.agentDisplayName = currentDisplayName;
              agentJoined = false;
            }
            if (agentJoined === false && packet.userId !== SYSTEM_TRIGGER_ADMIN) {
              // add 1s delay for the first agent message after joining
              toSend.timestamp = new Date(Date.now() + 1000);
              chatCallback({
                type: "chat.queue_position",
                nick: "system.queue",
                queue_position: 0,
                timestamp: new Date()
              });
              try {
                this.queuePositionSource?.close();
              } catch (err) {
                // pass through
              }
              chatCallback({
                type: "chat.memberjoin",
                nick: AGENT_NICK_DEFAULT,
                display_name: this.agentDisplayName,
                timestamp: new Date()
              });
              if (this.eventCallbacks.agent_update) {
                this.eventCallbacks.agent_update.forEach(
                  agentUpdateCallBack => {
                    if (agentUpdated === false) {
                      agentUpdateCallBack({
                        nick: AGENT_NICK_DEFAULT,
                        display_name: this.agentDisplayName,
                        timestamp: new Date()
                      });
                    }
                    agentUpdated = true;
                  }
                );
              }
              agentJoined = true;

              analytics.track({
                type: EVENT_TYPES.CHAT,
                name: CHAT_EVENTS.CHAT_ROOM_CHAT_MEMBER_JOINED,
              });
            }
            chatCallback(toSend);
          } else {
            chatCallback({
              type: 'chat.file',
              display_name: this.display_name,
              nick: 'visitor:',
              timestamp: packet.timestamp,
              attachment: toSend.attachment,
              source: 'local',
            });

            analytics.track({
              type: EVENT_TYPES.INPUT,
              name: INPUT_EVENTS.CHAT_ROOM_END_USER_SENT_ATTACHMENT,
            });
          }
        }
      });

    });

    this.firebaseEndedChatsDatabaseRef.once("value", snapshot => {
      if (this.eventCallbacks.chat) {
        const val = snapshot.val();
        if (val) {
          this.eventCallbacks.chat.forEach(chatCallback => {
            debounce(
              chatCallback({
                type: "chat.memberleave",
                nick: val.name || AGENT_NICK_DEFAULT,
                display_name: this.agentDisplayName,
                timestamp: new Date()
              })
            );
          });

          analytics.track({
            type: EVENT_TYPES.CHAT,
            name: CHAT_EVENTS.CHAT_ROOM_CHAT_MEMBER_LEFT,
          });
        }
      }
    });

    this.firebaseEndedChatsDatabaseRef.on("value", snapshot => {
      if (this.eventCallbacks.chat) {
        const val = snapshot.val();
        if (val) {
          this.eventCallbacks.chat.forEach(chatCallback => {
            // Not sure why this even exists
            debounce(
              chatCallback({
                // To avoid call multiple times
                type: "chat.memberleave",
                nick: val.name || AGENT_NICK_DEFAULT,
                display_name: this.agentDisplayName,
                timestamp: new Date()
              })
            );
          });

          analytics.track({
            type: EVENT_TYPES.CHAT,
            name: CHAT_EVENTS.CHAT_ROOM_CHAT_MEMBER_LEFT,
          });
        }
      }
    });

    return;
  }

  setupFirebaseAiChat(params: {
    aiSessionId: string;
    customerId: number;
    firebaseToken: string;
    onIntervene: (chatId: number) => void;
  }) {
    const { aiSessionId, customerId, firebaseToken, onIntervene } = params;

    this.aiSessionId = aiSessionId;
    this.customerId = customerId;
    this.firebaseToken = firebaseToken;

    this.firebaseApp = firebase.initializeApp(
      {
        apiKey: this.firebaseToken,
        authDomain: env.get('firebase').auth,
        databaseURL: env.get('firebase').database,
        projectId: env.get('firebase').projectId
      },
      `ai-chat-${this.aiSessionId}`
    );

    this.firebaseAiChatEndUserStatusRef = this.firebaseApp.database().ref(`aiChatEndUserStatus/${this.aiSessionId}`);

    // Setup Firebase user status without event callbacks
    this.setupFirebaseUserStatus({
      ref: this.firebaseAiChatEndUserStatusRef,
      chatId: this.aiSessionId,
      customerId: this.customerId
    });

    this.firebaseAiChatInterveneRef = this.firebaseApp.database().ref(`aiChatIntervention/${this.aiSessionId}`);

    // On value call onIntervene if it's not null
    this.firebaseAiChatInterveneRef.on('value', snap => {
      if (snap.exists() && snap.val() !== null) {
        onIntervene?.(snap.val().chatId);
      }
    });

    return;
  }
  // [Chat] Callback for Firebase realtime DB
  on(event: string, callback: Function) {
    if (typeof this.eventCallbacks[event] === "undefined") {
      this.eventCallbacks[event] = [];
    }
    this.eventCallbacks[event].push(callback);

    return this;
  }

  // [Chat] call when end chat
  async endChat(options?: { sessionId?: string }, callback?: Function) {
    let error = null;

    try {
      this.queuePositionSource?.close();
      this.systemMessageBroker?.destroy();
      this.systemMessageBroker = null;
    } catch (err) {
      // pass through
    }
    
    if (this.chatId) {
      await PleasedApi.reportChatEnded.once({ 
        chatId: this.chatId, 
        ...(options?.sessionId ? { aiSessionId: options.sessionId } : {}) 
      }).catch((e) => {
        console.error("Error in endChat", e);
        return;
      })
    }

    try {
      warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
      this.firebaseEndUserStatusRef?.update({ 
        status: "offline",         
        chatId: this.chatId,
        customerId: this.customerId, 
        lastStatusUpdateTime: firebase.database.ServerValue.TIMESTAMP 
      }, err => {
        if (err) {
          console.error(err);
        }
      });
      this.firebaseEndUserStatusRef?.onDisconnect().cancel();
      this.userStatusPollingId && clearInterval(this.userStatusPollingId);
    } catch (err) {
      error = err;
    }
    if (callback) callback(error);
    return this;
  }

  // [Chat] user is onTyping
  notifyUserTyping() {
    if (this.eventCallbacks.onTypingListChange) {
      this.eventCallbacks.onTypingListChange.forEach(callback => {
        callback(typeof structuredClone === 'function' ? structuredClone(this.usersTypingList) : {...this.usersTypingList});
      });
    }
    return this;
  }

  // [Chat] update user is typing on Firebase
  sendTypingPleased(message: string) {
    const userObject: UserTypingList = {};
    userObject[this.customerId] = {
      displayName: "Customer",
      isAgent: false,
      isTyping: false
    };
    if (message !== "") {
      if (
        this.usersTypingList[this.customerId] === undefined ||
        (this.usersTypingList[this.customerId] &&
          this.usersTypingList[this.customerId].isTyping === false)
      ) {
        userObject[this.customerId].isTyping = true;

        warnNonNull(this.firebaseUserTypingRef, 'this.firebaseUserTypingRef'); // TODO: RNL
        this.firebaseUserTypingRef?.set({
          ...this.usersTypingList,
          ...userObject
        });
      }
    } else if (this.usersTypingList[this.customerId].isTyping === true) {
      userObject[this.customerId].isTyping = false;
      warnNonNull(this.firebaseEndUserStatusRef, 'this.firebaseEndUserStatusRef'); // TODO: RNL
      this.firebaseUserTypingRef?.set({
        ...this.usersTypingList,
        ...userObject
      });
    }

    return this;
  }

  sendTyping(un?: any) {
    return this;
  }

  // [Chat] call when end chat
  disablePleasedTypingListener() {
    if (
      this.usersTypingList[this.customerId] &&
      this.usersTypingList[this.customerId].isTyping === true
    ) {
      warnNonNull(this.firebaseUserTypingRef, 'this.firebaseUserTypingRef'); // TODO: RNL
      this.firebaseUserTypingRef?.set({
        ...this.usersTypingList,
        [this.customerId]: {
          displayName: "Customer",
          isAgent: false,
          isTyping: false
        }
      });
    }
    warnNonNull(this.firebaseUserTypingRef, 'this.firebaseUserTypingRef'); // TODO: RNL
    this.firebaseUserTypingRef?.off();
  }

  // [Chat] end point: send message to /chat/${chatId} on Firebase
  sendChatMsg(params: {
    message: string,
    attachment?: MessageAttachment,
    userSelection?: boolean,
    meta?: Record<string, unknown>,
    callback?: (err: Error | null) => void,
    onComplete?: (err: Error | null) => void
  }) {
    const {
      message,
      attachment,
      userSelection = false,
      meta = {},
      callback,
      onComplete
    } = params;

    const chatMessage: FirebaseChatMessage = {
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      from: "customer",
      attachmentFlag: !!attachment,
      attachmentUrl: attachment?.url || "",
      attachmentName: attachment?.fileName || "",
      attachmentId: attachment?.id || "",
      contentType: attachment?.contentType || "",
      userId: this.customerId,
      message,
      to: "agent",
      userSelection,
      ...(meta ? meta : {})
    }
    
    try {
      warnNonNull(this.firebaseDatabaseRef, 'this.firebaseDatabaseRef'); // TODO: RNL
      const newRef = this.firebaseDatabaseRef?.push();
      if (newRef) {
        // If we need to check for write to server – use onComplete callback
        newRef.set(chatMessage, onComplete);
      } else {
        throw new Error('Pleased Client is not ready.')
      }
      // Here we check only if Client is ready and execute callback
      if (callback) callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));

      // Ignore
      this.unsentMessages.push(chatMessage);
      if (!this.endChatTicketSubjectSet) {
        this.endChatTicketSubject = message;
        this.endChatTicketSubjectSet = true;
      }
      if (callback) callback(error);
    }

    return this;
  }

  // [Chat] end point: send File to CDN, and update /chat/${chatId} on Firebase
  sendFile(fileStream: any, callback: Function) {
    getFileBuffer(fileStream).then(result => {
      this.sendFileInternal(result, fileStream, callback);
    });
    return this;
  }

  sendFileInternal(
    file: ArrayBuffer | string | null,
    fileStream: File,
    callback?: Function
  ) {
    const fileurl = "/api/v1/attachment/ticket_comment";
    const encodedFileName = Base64.encode(fileStream.name);
    this.axios
      .post(fileurl, file, {
        headers: {
          "Content-Type": "application/octet-stream",
          "file-name": encodedFileName || "unknown",
          "is-inline": false,
          "base64-file-name": encodedFileName
        }
      })
      .then(response => {
        if (response && response.data && response.data.code !== 10000) {
          const error = new Error(
            `Bad Response from Server; Code = ${response.data.code}`
          );
          return callback && callback(error);
        }

        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.url
        ) {
          try {
            warnNonNull(this.firebaseDatabaseRef, 'this.firebaseDatabaseRef'); // TODO: RNL
            const newRef = this.firebaseDatabaseRef?.push();
            newRef?.set({
              createdAt: firebase.database.ServerValue.TIMESTAMP,
              from: "customer",
              attachmentFlag: true,
              attachmentUrl: response.data.data.url,
              attachmentName: response.data.data.fileName,
              attachmentId: response.data.data.id,
              userId: this.customerId,
              message: "",
              to: "agent",
              contentType: response.data.data.contentType
            });
          } catch (error) {
            // Ignore
          }
          if (callback) callback();
        }
      })
      .catch(axiosError => {
        const error = axiosError;
        callback && callback(error);
      });
  }

  isReady() {
    if (this.eventCallbacks.ready_to_chat) {
      this.eventCallbacks.ready_to_chat.forEach(callback => {
        callback(true);
      });
    }
  }

  // [Chat] end point: get feedback options, the options now is hardcoded on BE
  getFeedbackOption() {
    return this.axios
      .get("/api/v1/satisfaction/feedback-selections")
      .then(res => {
        if (res && res.data && res.data.code !== 10000) {
          return [];
        }
        return res.data.data;
      });
  }

  // [Chat] end point: send Feedback
  postFeedback(obj: {
    rating?: string;
    comment: string;
    feedbackSelectionIds: string;
  }) {
    const { rating, comment, feedbackSelectionIds } = obj;
    const body = {
      rating,
      comment,
      feedbackSelectionIds
    };
    return this.axios.post(
      `/api/v1/satisfaction/chat/${this.chatId}/review/`,
      body
    );
  }

  // [Voice] request voice, and voice related handler
  requestVoice({
    description = "",
    isGuest = true,
    // Is end user is guest: phone / email in visitorInfo in class PleasedChatClient will be null as below codebase
    //
    // (src/views/Chatroom/index.vue)
    // let visitorInfo = {
    //   display_name: this.userPhone ? this.userPhone : this.visitorInfo.displayName,
    //   foreign_id: res.data.id,
    //   phone: res.data.phone ? res.data.phone : null,
    //   email: this.isEmailVerified ? this.visitorInfo.email : null,
    //   customerAppVersion: this.customerAppVersion,
    // };
    guestObj = {
      id: '',
      phone: '',
      email: '',
    },
    autoAddTags = [],
    chatTags,
    extraFields = [],
    callbacks = {},
    ticketId,
    aiSessionId
  }: {
    description?: string;
    isGuest?: boolean;
    guestObj?: {
      id: string;
      phone: string;
      email: string;
    };
    autoAddTags?: string[];
    chatTags?: string[];
    callbacks?: {
      onError?: (err: any) => any;
      onReady?: (device: any) => any;
      onConnect?: (connection: any) => any;
      onDisconnect?: (connection: any) => any;
    };
    extraFields?: { attributeKey: string, attributeValue: string }[];
    ticketId?: string | number
    aiSessionId?: string;
  } = {}) {
    reportingService.voiceChatInitiated()
    event(GA_EVENT_NAME_ENUM.START_VOICE_CHAT_CONNECTION);

    const tags = [...autoAddTags, ...(chatTags?.length ? Tags.clean(chatTags) : [])];

    const body = {
      userDetails: {
        email: isGuest ? guestObj.email : this.email,
        foreignId: this.foreignId,
        name: this.name,
        phone: isGuest ? guestObj.phone : this.phone,
        isGuest,
        ...(aiSessionId && isGuest ? { userId: guestObj.id } : {}),
      },
      tags,
      extraFields,
      description,
      ...(ticketId ? { ticketId } : null),
      ...(reportingService.getLogs().length ? { userLogs: reportingService.getLogs() } : null),
      chatbotSessionId: analytics.sessionId,
      aiSessionId,
    };

    analytics.trackVoiceEvent({
      name: `${CMP_CONTEXTS.CONNECT_REQUEST}${COMPONENT_EVENTS.CONNECT_OPTIONS_CALL_INITIATED}`,
      selection: ticketId || null,
      metadata: { body },
    });

    return (
      this.axios
        .post("/api/v1/voice-chat/request", body)
        // .then(res => res.json())
        .then(res => {
          const { code, data } = res.data;

          if (code === ErrorCode.THRESHOLD_LIMIT) {
            throw new ThresholdLimitError();
          }

          if (code === 10000 && data.twilioJWT) {
            const twilioDevice = new Device(data.twilioJWT, {
              // Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
              // providing better audio quality in restrained network conditions. Opus will be default in 2.0.
              codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
              enableImprovedSignalingErrorPrecision: true,
              maxCallSignalingTimeoutMs: 30000
            });

            twilioDevice.on('registering', () => {
              console.log('[twilio] registering: ');
              event(GA_EVENT_NAME_ENUM.VOICE_CHAT_REGISTERING);
              analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTERING });
            });

            twilioDevice.on("registered", async device => {
              console.log('[twilio] registered');
              event(GA_EVENT_NAME_ENUM.VOICE_CHAT_REGISTERED);
              analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTERED });
                try {
                  this.call = await twilioDevice?.connect();
                  callbacks.onReady && callbacks.onReady(device);
                  console.log('[twilio] connected');
                  event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CONNECTED_SUCCESSFUL);
                  analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_CONNECTED_SUCCESSFUL });
                  this.handleCallAcceptance(ticketId, callbacks, data);
                } catch (e) {
                  console.log('[twilio] connection failed', e);
                  event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CONNECTED_FAILED);
                  analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_CONNECTED_FAILED, metadata: { error: parseErrorMessage(e) } });
                }
            });

            twilioDevice.on("error", function (error) {
              // TODO: error handler
              console.log('[twilio] error: ', error.message);
              event(GA_EVENT_NAME_ENUM.VOICE_CHAT_ERROR);
              analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_ERROR, metadata: { error: parseErrorMessage(error) } });
              callbacks.onError && callbacks.onError(error);
            });

            twilioDevice.on('unregistered', () => {
              console.log('[twilio] unregistered');
              event(GA_EVENT_NAME_ENUM.VOICE_CHAT_UNREGISTERED);
              analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_UNREGISTERED });
            });

            this.twilioDevice = twilioDevice;
            this.twilioDevice?.register();
            event(GA_EVENT_NAME_ENUM.VOICE_CHAT_REGISTER);
            analytics.trackVoiceEvent({ name: VOICE_EVENTS.DEVICE_REGISTER });
          } else {
            analytics.trackCriticalEvent({
              name: CRITICAL_EVENTS.CONNECT_REQUEST_MESSAGE_SEND_ERROR,
              metadata: {
                data: res.data,
              },
            });
          }
          
          return res.data;
        })
    );
  }

  handleCallAcceptance(ticketId: any, callbacks: any, data: any) {
    // Reset silent call tracking for new call
    this.silentCallCount = 0;
    this.silentCallEventSent = false;
    this.hasNonEmptySampleBeforeThreshold = false;
    
    if (this.call) {
      this.call.on('accept', (conn) => {
        console.log('[twilio][call] accept: ', conn);
        const context = ticketId ? CMP_CONTEXTS.ACTIVE_CONVERSATION_TICKET : CMP_CONTEXTS.CONNECT_REQUEST;

        analytics.track({
          name: `${context}${COMPONENT_EVENTS.CONNECT_OPTIONS_CALL_CREATED}`,
          // TODO: check if data has id
          ...(ticketId ? { selection: ticketId } : { selection: data?.id }),
        })
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_ACCEPTED);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_ACCEPTED });
        callbacks.onConnect && callbacks.onConnect(conn);
      });

      this.call.on('error', (error: any) => {
        console.log('[twilio][call] error: ', error.message);
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_ERROR);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_ERROR, metadata: { error: parseErrorMessage(error) } });
        callbacks.onError && callbacks.onError(error);
      });

      this.call.on('cancel', () => {
        console.log('[twilio][call] cancel');
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_CANCEL);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_CANCEL });
      });

      this.call.on('reconnecting', (twilioError: any) => {
        console.log('[twilio][call] reconnecting: ', twilioError);
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_RECONNECTING);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_RECONNECTING });

      });

      this.call.on('reconnected', () => {
        console.log('[twilio][call] reconnected');
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_RECONNECTED);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_RECONNECTED });
      });

      this.call.on('disconnect', (conn) => {
        console.log('[twilio][call] disconnect', conn);
        this.twilioDevice && this.twilioDevice.disconnectAll();
        this.twilioDevice && this.twilioDevice.destroy();
        callbacks.onDisconnect && callbacks.onDisconnect(conn);
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_DISCONNECTED);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_DISCONNECTED });
      });

      let voiceSampleCount = 0;
      // Subscribe to "sample" event, which sends data every 1s, so we need to throttle
      const throttledSampleHandler = throttle((sampleData: any) => {
        if (voiceSampleCount >= VOICE_SAMPLE_MAX_SAMPLES) return; // skip if limit exceeded
        voiceSampleCount += 1;
        event(GA_EVENT_NAME_ENUM.VOICE_CHAT_CALL_SAMPLE);
        analytics.trackVoiceEvent({ name: VOICE_EVENTS.CALL_SAMPLE, metadata: sampleData });
      }, VOICE_SAMPLE_TRACK_INTERVAL);

      this.call.on('sample', throttledSampleHandler);

      // Un-throttled sample handler for silent call detection
      this.call.on('sample', (sampleData: any) => {
        const isEmpty = !sampleData.packetsSent && !sampleData.bytesSent;

        // Skip if event already sent or we have seen a non-empty sample before threshold
        if (this.silentCallEventSent || this.hasNonEmptySampleBeforeThreshold) return;

        if (!isEmpty) {
          this.hasNonEmptySampleBeforeThreshold = true;
          this.silentCallCount = 0;
          return
        }
        

        this.silentCallCount++;
        // Send SILENT_CALL event if threshold exceeded and not already sent for this call
        if (this.silentCallCount >= SILENT_CALL_THRESHOLD && !this.silentCallEventSent) {
          this.silentCallEventSent = true;
          analytics.trackVoiceEvent({ name: VOICE_EVENTS.SILENT_CALL });
        }
      });
    }
    
  }

  // [Voice] hangup
  hangUpVoice() {
    this.twilioDevice && this.twilioDevice.disconnectAll();
    this.twilioDevice && this.twilioDevice.destroy();
  }

  // [Voice] to see if the device is supported
  static get isVoiceChatSupported() {
    const twilioSupported = Device.isSupported;
    return twilioSupported;
  }


  // [Note] all iOS webview is unsupported WebRTC
  static get isVoiceChatSupportedInApp() {
    const twilioSupported = Device.isSupported;

    if (!twilioSupported) return false;

    const details = getIOSDetails();

    if (!details) return twilioSupported;

    // Detected iOS iPhone, we will support >= 15.6 (Tests shows that 15.6 works as expected.)
    // iOS 15 has issues with websocket support: https://developer.apple.com/forums/thread/685403
    return details.minorNum >= 15.6;
  }

  /**
   * [Voice] Check if there are any agents online to accept a voice call
   * request.
   * @returns True if any agents are online.
   */
  async isVoiceCallAgentOnline() {
    const response = await this.axios.get<{ code: number; data: { onlineAgents: boolean; }; }>("/api/v1/voice-chat/online-agents");
    return (
      response.data &&
      response.data.code === 10000 &&
      response.data.data.onlineAgents
    );
  }
}

export default PleasedChatClient;
